import React from "react";
import styled from "@emotion/styled/macro";
import SelectInput from "../../../Utils/FormInputs/SelectInput";

/* ─── Styled Components ─── */
const ApgarWrapper = styled("div")({
    background: "#fff",
    borderRadius: "10px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    marginBottom: "14px",
    overflow: "hidden",
});

const ApgarHeader = styled("div")({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px 18px",
    borderBottom: "1px solid #f0f0f0",
    flexWrap: "wrap",
    gap: "8px",
});

const ApgarTitle = styled("h3")({
    margin: 0,
    fontSize: "0.95rem",
    fontWeight: 700,
    color: "#283593",
});

const ScoreSummary = styled("div")({
    display: "flex",
    gap: "12px",
    flexWrap: "wrap",
    fontSize: "0.82rem",
    fontWeight: 600,
});

const ScoreSummaryItem = styled("span")(({ color }) => ({
    color: color || "#999",
}));

/* ── Table Styles ── */
const ApgarTable = styled("table")({
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "0.82rem",
});

const ApgarThead = styled("thead")({
    background: "#f5f6fa",
});

const ThCell = styled("th")(({ center }) => ({
    padding: "10px 14px",
    textAlign: center ? "center" : "left",
    fontWeight: 700,
    fontSize: "0.78rem",
    color: "#555",
    borderBottom: "2px solid #e0e0e0",
    whiteSpace: "nowrap",
}));

const TdCell = styled("td")(({ center }) => ({
    padding: "10px 14px",
    borderBottom: "1px solid #f0f0f0",
    verticalAlign: "middle",
    textAlign: center ? "center" : "left",
}));

const CriterionLabel = styled("div")({
    fontWeight: 600,
    color: "#333",
    fontSize: "0.84rem",
    marginBottom: "2px",
});

const CriterionSub = styled("div")({
    fontSize: "0.72rem",
    color: "#888",
    fontWeight: 400,
});

const TotalRow = styled("tr")({
    background: "#f5f6ff",
});

const TotalCell = styled("td")(({ color }) => ({
    padding: "12px 14px",
    fontWeight: 800,
    fontSize: "1.1rem",
    textAlign: "center",
    color: color || "#283593",
    borderTop: "2px solid #283593",
}));

const TotalLabel = styled("td")({
    padding: "12px 14px",
    fontWeight: 700,
    fontSize: "0.9rem",
    color: "#283593",
    borderTop: "2px solid #283593",
});

const ClassLabel = styled("div")(({ color }) => ({
    fontSize: "0.72rem",
    fontWeight: 600,
    color: color || "#888",
}));

/* ─── Component ─── */
export const APGARCalculator = ({ nb, show10min = false }) => {
    const CRITERIA = nb.APGAR_CRITERIA;
    const minutes = show10min ? ["1min", "5min", "10min"] : ["1min", "5min"];
    const minuteLabels = { "1min": "1 Minute", "5min": "5 Minutes", "10min": "10 Minutes" };

    return (
        <ApgarWrapper>
            <ApgarHeader>
                <ApgarTitle>APGAR Score Calculator</ApgarTitle>
                <ScoreSummary>
                    {minutes.map((m) => {
                        const t = nb.apgarTotals[m];
                        const color = t.complete ? nb.getApgarColor(t.total) : "#999";
                        return (
                            <ScoreSummaryItem key={m} color={t.complete ? color : "#999"}>
                                {minuteLabels[m]}: {t.complete ? `${t.total}/10 (${nb.getApgarLabel(t.total)})` : "—"}
                            </ScoreSummaryItem>
                        );
                    })}
                </ScoreSummary>
            </ApgarHeader>
            <div style={{ overflowX: "auto" }}>
                <ApgarTable>
                    <ApgarThead>
                        <tr>
                            <ThCell>Criterion</ThCell>
                            {minutes.map((m) => (
                                <ThCell key={m} center style={{ minWidth: "200px" }}>
                                    {minuteLabels[m]}
                                </ThCell>
                            ))}
                        </tr>
                    </ApgarThead>
                    <tbody>
                        {CRITERIA.map((criterion) => {
                            const selectOptions = criterion.options.map((opt) => ({
                                value: String(opt.score),
                                text: `${opt.score} — ${opt.label}`,
                            }));

                            return (
                                <tr key={criterion.key}>
                                    <TdCell>
                                        <CriterionLabel>{criterion.label.split("(")[0].trim()}</CriterionLabel>
                                        {criterion.label.indexOf("(") >= 0 && (
                                            <CriterionSub>{"(" + criterion.label.split("(")[1]}</CriterionSub>
                                        )}
                                    </TdCell>
                                    {minutes.map((m) => {
                                        const currentScore = nb.apgar[m][criterion.key];
                                        return (
                                            <TdCell key={m} center>
                                                <SelectInput
                                                    name={`apgar_${m}_${criterion.key}`}
                                                    value={currentScore !== null && currentScore !== undefined ? String(currentScore) : ""}
                                                    onChange={(ev) => {
                                                        const val = ev.target.value;
                                                        if (val !== "") {
                                                            nb.setApgarScore(m, criterion.key, parseInt(val, 10));
                                                        }
                                                    }}
                                                    options={selectOptions}
                                                    defaultOption="— Select —"
                                                />
                                            </TdCell>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                        {/* Total row */}
                        <TotalRow>
                            <TotalLabel>TOTAL</TotalLabel>
                            {minutes.map((m) => {
                                const t = nb.apgarTotals[m];
                                const color = t.complete ? nb.getApgarColor(t.total) : "#999";
                                return (
                                    <TotalCell key={m} color={t.complete ? color : "#999"}>
                                        {t.complete ? t.total + "/10" : "—/10"}
                                        {t.complete
                                            ? <ClassLabel color={color}>{nb.getApgarLabel(t.total)}</ClassLabel>
                                            : <ClassLabel>Incomplete</ClassLabel>}
                                    </TotalCell>
                                );
                            })}
                        </TotalRow>
                    </tbody>
                </ApgarTable>
            </div>
        </ApgarWrapper>
    );
};
